import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { FontSize } from '../../constants/type-constants';
import { useStore } from '../../contexts/store-provider';
import { getTenantIdFromLocalStorage } from '../../helpers/localstorage-helper';
import FormLoader from '../../shared-components/FormLoader/FormLoader';
import RoutesConstants from '../../shared-components/routes/routes-constants';
import { formatMessage } from '../../translations/format-message';
import PrintContent from './components/print-content';
import RemoveTask from './components/remove-task';
import PrintContentPopupHOC from './hoc/print-popup-hoc';
import RemoveTaskPopupHOC from './hoc/remove-task-popup-hoc';
import ModeSelectionModal from './components/mode-selection-modal';
import './operator.css';
import { ProcessTypeEnum } from '../../constants/enums';
import { IFileListItem } from '../../models/response/IOperatorAssignedTaskResponse';
import OperatorAssignSingleMode from './modes/operator-assign-single-mode';
import OperatorAssignBatchMode from './modes/operator-assign-batch-mode';
import { useOperatorAssignSingle } from './OperatorAssignSingle';
import { useOperatorAssignBatch } from './OperatorAssignBatch';
import config from '../../helpers/config-helper';
import worker_script from './../../shared-components/web-worker/webWorker';
import LedBlink from '../dashboards/order-table-view/components/led-blink';
import LEDBlinkPopupHOC from '../dashboards/order-table-view/hoc/led-blink-popup-hoc';
import CorrectionRequestPopupHOC from './hoc/request-correction-popup-hoc';
import CorrectionRequest from './components/correction-request';
import NonConformityPopupHOC from './hoc/non-conformity-popup-hoc';
import AddUpdateNonConformity from '../../shared-components/add-update-non-conformity/add-update-non-conformity';
import ProcessInUseConfirmPopup from './components/process-in-use-confirm-popup';
import ProcessPauseConfirmPopup from './components/process-pause-confirm-popup';
import {
  OperatorTaskProvider,
  OperatorTaskContextType,
} from './context/operator-task-context';

const appConfig = config();
const showAddTrayInput = appConfig.REACT_APP_SHOW_ADD_TRAY_INPUT;
const EMPTY_FILE_LIST: IFileListItem[] = [];
const timerWorker = new Worker(worker_script);

const parseDimensionValue = (rawValue: any) => {
  let value =
    rawValue === null || rawValue === undefined ? '' : String(rawValue).trim();
  let isDefaultChecked = false;
  const lastIndex = value.lastIndexOf('$');

  if (lastIndex !== -1) {
    const suffix = value.substring(lastIndex + 1);
    if (suffix === 'true' || suffix === 'false') {
      isDefaultChecked = suffix === 'true';
      value = value.substring(0, lastIndex).trim();
    }
  }

  return {
    value,
    hasValue: value !== '' && value !== '-' && value !== '--',
    isDefaultChecked,
  };
};

const getDimensionCheckKey = (
  tabIndex: number,
  rowIndex: number,
  dimId: number
) => `${tabIndex}-${rowIndex}-${dimId}`;

const getLegacyDimensionCheckKey = (rowIndex: number, dimId: number) =>
  `${rowIndex}-${dimId}`;

const getEffectiveDimensionChecked = (
  dimensionChecks: Record<string, boolean>,
  tabIndex: number,
  rowIndex: number,
  dimId: number,
  isDefaultChecked: boolean
) => {
  const scopedKey = getDimensionCheckKey(tabIndex, rowIndex, dimId);
  const legacyKey = getLegacyDimensionCheckKey(rowIndex, dimId);

  if (dimensionChecks[scopedKey] !== undefined) {
    return dimensionChecks[scopedKey];
  }

  if (dimensionChecks[legacyKey] !== undefined) {
    return dimensionChecks[legacyKey];
  }

  return isDefaultChecked;
};

const OperatorAssignTask = (props: any) => {
  const {
    operatorStore,
    preferencesStore,
    dashboardStore,
    correctionControlStore,
    nonconformityStore,
    toolBOMStore,
  } = useStore();
  const {
    CompleteProcessService,
    completeProcessState,
    GetOperatorService,
    getOperatorState,
    OperatorResponse,
    resetCompleteProcessState,
    resetOperatorResponse,
    removeForAbnormalityState,
    resetRemoveForAbnormalityState,
    operatorTeplarDetails,
    resetGetOperatorState,
    UpdateProcessService,
    updateProcessState,
    resetUpdateProcessState,
    bomDataList,
    SaveTeplarOrderService,
    saveTeplarOrderState,
    resetSaveTeplarOrderState,
    ledCutList,
    ledLengthList,
    CheckProcessStatus,
    checkProcessStatusState,
    resetCheckProcessStatusState,
    inProgressOrCompletedTrays,
    completedTrays,
    isPaused,
    isTimerRunning,
    setIsTimerRunning,
  } = operatorStore;

  const {
    addCorrectionRequestState,
    resetAddCorrectionRequestState,
    GetPendingCountService,
  } = correctionControlStore;

  const {
    addNCRequestState,
    resetAddNCRequestState,
    SubmitNonConformanceWork,
    submitNonConformanceWorkState,
    resetSubmitNCWorkState,
    GetNonConformanceByIdService,
    getNonConformanceByIdState,
    nonConformity,
    resetNonConformity,
  } = nonconformityStore;

  const { ledBlinkState, resetLedBlinkState } = dashboardStore;
  const { language } = preferencesStore;
  const { taskId } = useParams<any>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const processName = queryParams.get('processName');
  const processType = ProcessTypeEnum[queryParams.get('processType')];
  const [operatorCount, setOperatorCount] = useState<number>(1);
  const [isBatch, setIsBatch] = useState<boolean | null>(null); //local
  const [startDateTime, setStartDateTime] = useState(null); //local
  const [timer, setTimer] = useState<number>(0); //global
  // const [isTimerRunning, setIsTimerRunning] = useState<boolean>(false); //global
  const [isProcessStarted, setIsProcessStarted] = useState(false); //global
  const [showDraggableStart, setShowDraggableStart] = useState(true); //global
  const [scanBuffer, setScanBuffer] = useState(''); //global
  const [lastKeyTime, setLastKeyTime] = useState(Date.now()); //global
  const [trayIndex, setTrayIndex] = useState(0); //global
  const [trayHistory, setTrayHistory] = useState<string[]>([]); //global
  const [selectedTrayId, setSelectedTrayId] = useState<string | null>(null); //global
  const [activeIndex, setActiveIndex] = useState<number>(0); //global
  const [trayId, setTrayId] = useState(''); //remove later//global
  const [printContents, setPrintContents] = useState<string[]>([]); //local
  const [templateName, setTemplateName] = useState<string>(''); //local
  const [startTimer, setStartTimer] = useState<number>(0); //global
  const [removeDuration, setRemoveDuration] = useState<number>(0); //used only in single mode
  const [dimensionChecks, setDimensionChecks] = useState<
    Record<string, boolean>
  >({}); //global
  const [memo, setMemo] = useState<Record<number, string>>({}); //global
  const [webWorkerTime, setWebWorkerTime] = useState(0); //global
  const [showModeSelectionPopup, setShowModeSelectionPopup] = useState(false); //local
  const [teplarOrderData, setTeplarOrderData] = useState<
    { Id: number; Order: number }[]
  >([]); //local
  const [saveClicked, setSaveClicked] = useState(false); //global
  const traySelectRef = useRef<HTMLButtonElement>(null); //global

  type CheckItemState = {
    isChecked?: boolean;
    InputMessage?: string;
  };

  type CheckState = {
    [uiTabIndex: number]: {
      [checkItemId: number]: CheckItemState;
    };
  };

  const [submittedCheckItems, setSubmittedCheckItems] = useState<CheckState>(
    {}
  );
  const [canvasDrawings, setCanvasDrawings] = useState<Record<string, string>>(
    {}
  );

  const [bomDataListState, setBomDataListState] = useState<any[]>([]);
  const [ledCutListState, setLedCutListState] = useState<any[]>([]);
  const [ledLengthListState, setLedLengthListState] = useState<any[]>([]);

  const [teplarList, setTeplarList] = useState<any[]>([]);
  const [isAddTrayInput, setIsAddTrayInput] = useState(false);
  const [isRemoveForAbnormalityClicked, setIsRemoveForAbnormalityClicked] =
    useState(false);
  const [openNonConformity, setOpenNonConformity] = useState(false);
  const [isNonConformity, setIsNonConformity] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCompletedProcessErrorModal, setShowCompletedProcessErrorModal] =
    useState(false);
  const [showConfirmSubmitModal, setShowConfirmSubmitModal] = useState(false);
  const [showStart, setShowStart] = useState(false);

  const productImages: IFileListItem[] =
    OperatorResponse.FileList ?? EMPTY_FILE_LIST;
  // Calculate the correct FileList index (subtract 1 because mergedTabs includes dimension table at index 0)

  const checkItems: [] =
    OperatorResponse.FileList && activeIndex >= 0
      ? OperatorResponse.FileList[activeIndex]?.CheckItems
      : [];

  const dims = OperatorResponse?.FileList?.[activeIndex]?.DimensionsData ?? [];

  const dimensionTable = (() => {
    const maxValueRows = Math.max(
      ...dims.map((d) => d.DimensionValues?.length ?? 0),
      0
    );

    // ✅ if headers exist but no values → show one "--" row
    const totalRows = dims.length > 0 ? Math.max(maxValueRows, 1) : 0;

    return Array.from({ length: totalRows }, (_, i) => {
      const row: any = {};

      dims.forEach((dim) => {
        const hasValue =
          Array.isArray(dim.DimensionValues) && i < dim.DimensionValues.length;

        let rawValue = hasValue ? dim.DimensionValues[i] : '-';
        let val = rawValue?.toString() ?? '-';

        let isChecked = false;
        const lastIndex = val.lastIndexOf('$');

        if (lastIndex !== -1) {
          const suffix = val.substring(lastIndex + 1);
          if (suffix === 'true' || suffix === 'false') {
            isChecked = suffix === 'true';
            val = val.substring(0, lastIndex);
          }
        }

        if (val.trim() === '') {
          val = '-';
        }

        row[dim.DimensionColumn] = {
          value: val,
          isDefaultChecked: isChecked,
        };
      });

      return row;
    });
  })();

  const [printCount, setPrintCount] = useState<number[]>([]);

  const history = useHistory();

  const handleDecrement = () => {
    setOperatorCount((prev) => Math.max(0, prev - 1));
  };

  const handleIncrement = () => {
    setOperatorCount((prev) => prev + 1);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 0) {
      setOperatorCount(value);
    }
  };

  const handleIncrementPrintCount = (index: number) => {
    setPrintCount((prev) => {
      const newPrintCount = [...prev];
      newPrintCount[index] = (newPrintCount[index] || 0) + 1;
      return newPrintCount;
    });
  };
  // const triggerDidPrint = (href: string) => {
  //   try {
  //     const iframe = document.createElement('iframe');
  //     iframe.style.display = 'none';
  //     iframe.src = href;
  //     document.body.appendChild(iframe);

  //     setTimeout(() => {
  //       document.body.removeChild(iframe);
  //     }, 1000);
  //   } catch {
  //     // Even if native handler is missing, app remains stable
  //     toast.error('Print service is not available');
  //   }
  // };
  const handleDecrementPrintCount = (index: number) => {
    setPrintCount((prev) => {
      const newPrintCount = [...prev];
      newPrintCount[index] = (newPrintCount[index] || 0) - 1;
      return newPrintCount;
    });
  };

  useEffect(() => {
    timerWorker.onmessage = ({ data: { time } }) => {
      setWebWorkerTime(time);
    };
  }, []);

  const startWebWorkerTimer = (initialTime = webWorkerTime) => {
    timerWorker.postMessage({ turn: 'on', initialTime });
  };

  const stopWebWorkerTimer = () => {
    timerWorker.postMessage({ turn: 'off' });
  };

  const resetWebWorkerTimer = () => {
    timerWorker.postMessage({ turn: 'reset' });
    setWebWorkerTime(0);
  };

  useEffect(() => {
    if (operatorTeplarDetails.length > 0) {
      let arr = Array.from({ length: length }, (_, index) => 1);
      setPrintCount(arr);
    }
  }, [operatorTeplarDetails]);

  const handleChangePrintCount = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const value = parseInt(e.target.value);
    setPrintCount((prev) => {
      const newPrintCount = [...prev];
      newPrintCount[index] = value;
      return newPrintCount;
    });
    // if (!isNaN(value) && value >= 0) {
    //   setPrintCount(value);
    // }
  };

  const stringToHex = (text) => {
    var bytes = new TextEncoder().encode(text);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join(
      ''
    );
  };

  const buildCurrentTeplarOrderPayload = () =>
    teplarList.map((item, index) => ({
      Id: item.Id,
      Order: index + 1,
    }));

  const handleSaveTeplarOrder = (
    trayTeplarOrders?: { Id: number; Order: number }[]
  ) => {
    const resolvedTrayTeplarOrders =
      trayTeplarOrders ?? buildCurrentTeplarOrderPayload();

    if (resolvedTrayTeplarOrders.length === 0) {
      return Promise.resolve();
    }

    const payload = {
      TenantId: parseInt(getTenantIdFromLocalStorage(), 10),
      TrayTeplarOrders: resolvedTrayTeplarOrders,
    };
    return SaveTeplarOrderService(payload);
  };

  const bulidCheckItemsPayload = () => {
    const fileList = OperatorResponse?.FileList ?? [];

    if (!fileList.length) return [];

    return fileList.flatMap((fileItem: any, fileIndex: number) => {
      const tabChecks = submittedCheckItems?.[fileIndex] ?? {};

      return (fileItem?.CheckItems ?? []).flatMap((group: any) => {
        return (group?.CheckItemMessage ?? [])
          .filter((item: any) => item?.Id != null)
          .map((item: any) => {
            const stateData = tabChecks[item.Id] ?? {};

            return {
              Id: item.Id,
              IsChecked:
                stateData.isChecked !== undefined
                  ? stateData.isChecked
                  : (item.IsChecked ?? false),
              InputMessage:
                stateData.InputMessage !== undefined
                  ? stateData.InputMessage
                  : (item.InputMessage ?? ''),
            };
          });
      });
    });
  };

  const buildMemoPayload = () => {
    return mergedTabs.map((tab, index) => {
      const memoKey = tab?.data?.TabIndex ?? index;
      const existingMemo =
        Array.isArray(tab?.data?.MemoMessage) && tab.data.MemoMessage.length > 0
          ? tab.data.MemoMessage[0]
          : null;

      return {
        Id: Number(existingMemo?.Id ?? 0),
        TrayId: selectedTrayId?.trim(),
        TabIndex: Number(memoKey),
        Memo: memo[memoKey] ?? existingMemo?.Memo ?? '',
      };
    });
  };

  const buildDimensionPayload = () => {
    const fileList = OperatorResponse?.FileList ?? [];

    return fileList.flatMap((file: any, fileIndex: number) => {
      const dimensions = file?.DimensionsData ?? [];

      return dimensions
        .filter((dim: any) => dim.DimensionType === 1)
        .map((dim: any) => {
          const valuesArray = (dim.DimensionValues ?? []).map(
            (rawValue: any, rowIndex: number) => {
              let value =
                rawValue === null || rawValue === undefined
                  ? ''
                  : String(rawValue);
              let isDefaultChecked = false;
              const lastIndex = value.lastIndexOf('$');

              if (lastIndex !== -1) {
                const suffix = value.substring(lastIndex + 1);
                if (suffix === 'true' || suffix === 'false') {
                  isDefaultChecked = suffix === 'true';
                  value = value.substring(0, lastIndex);
                }
              }

              if (value.trim() === '') {
                value = '-';
              }

              const checked = getEffectiveDimensionChecked(
                dimensionChecks,
                fileIndex,
                rowIndex,
                dim.Id,
                isDefaultChecked
              );
              return `${value}$${checked}`;
            }
          );

          return {
            Id: dim.Id,
            DimensionValue: valuesArray,
          };
        });
    });
  };

  const buildBOMCheckListPayload = () => {
    const bomRows =
      bomDataListState && bomDataListState.length > 0
        ? bomDataListState
        : bomDataList;
    const isTrue = (value: any) =>
      value === true || value === 1 || String(value).toLowerCase() === 'true';

    return (bomRows ?? []).map((item) => ({
      Id: item.Id,
      Pick: isTrue(item.Pick),
      Shortage: isTrue(item.Shortage),
    }));
  };

  const buildTeplarListPayload = () =>
    teplarList.map((item, index) => ({
      Id: item.Id,
      Order: index + 1,
      IsPrinted: Boolean(item.isPrinted || item.Printed),
    }));

  useEffect(() => {
    setShowModeSelectionPopup(true);
    localStorage.removeItem('trayData');
  }, []);

  useEffect(() => {
    if (isBatch !== null) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        toast.error(formatMessage('please_select_mode_before_scan'));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isBatch]);

  const formatTime = (seconds: number) => {
    const totalSeconds = Math.floor(seconds);
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    return [hrs, mins, secs].map((v) => String(v).padStart(2, '0')).join(':');
  };

  const getFontSizeClass = (size: FontSize) => {
    if (size === 'small') return 'opertor-small-text';
    if (size === 'large') return 'opertor-text-large';
    return 'opertor-normal-text';
  };
  const savedFontSize = localStorage.getItem('fontSize') as FontSize;
  const initialFontSize: FontSize =
    savedFontSize === 'small' || savedFontSize === 'large'
      ? savedFontSize
      : 'normal';

  const [fontSizeClass, setFontSizeClass] = useState<string>(
    getFontSizeClass(initialFontSize)
  );
  const [activeSize, setActiveSize] = useState<FontSize>(initialFontSize);

  const handleFontSizeChange = (size: FontSize) => {
    localStorage.setItem('fontSize', size);
    setActiveSize(size);

    setFontSizeClass(getFontSizeClass(size));
  };

  const handleMemoChange = (tabIndex: number, value: string) => {
    setMemo((prev) => ({
      ...prev,
      [tabIndex]: value,
    }));
  };

  const handleDimensionCheck = (
    tabIndex: number,
    rowIndex: number,
    dimId: number,
    checked: boolean
  ) => {
    const key = getDimensionCheckKey(tabIndex, rowIndex, dimId);
    setDimensionChecks((prev) => ({
      ...prev,
      [key]: checked,
    }));
  };

  const handleCanvasChange = (key: string, dataUrl: string) => {
    setCanvasDrawings((prev) => ({
      ...prev,
      [key]: dataUrl,
    }));
  };

  useEffect(() => {
    resetWebWorkerTimer();
    resetOperatorResponse();
  }, []);

  useEffect(() => {
    toolBOMStore.setCurrentProcessName(processName);
    return () => toolBOMStore.setCurrentProcessName('');
  }, [processName]);

  useEffect(() => {
    if (updateProcessState.success) {
      if (saveClicked) {
        toast.success(formatMessage('updated_success'));
      }
      // activeMode.handleStartTimer();
      setSaveClicked(false);
      resetUpdateProcessState();
    }
  }, [updateProcessState.success]);

  useEffect(() => {
    if (updateProcessState.error) {
      // props.confirmToggleHandler();
      // if(updateProcessState.error === "ProcessInUse") { //Take String to some const
      //   setShowConfirmModal(true);//<-- this will use ErrorMessageData
      // }
      // toast.error(formatMessage(updateProcessState.error));
      resetUpdateProcessState();
      //ARIF: ErrorMessageData = ""
    }
  }, [updateProcessState.error]);

  useEffect(() => {
    if (checkProcessStatusState.success) {
      // if (saveClicked) {
      //   toast.success(formatMessage('updated_success'));
      // }
      activeMode.handleStartTimer();
      // setSaveClicked(false);
      resetCheckProcessStatusState();
    }
  }, [checkProcessStatusState.success]);

  useEffect(() => {
    if (checkProcessStatusState.error) {
      // props.confirmToggleHandler();
      if (checkProcessStatusState.error === 'ProcessAlreadyCompleted') {
        setShowCompletedProcessErrorModal(true);
        setShowConfirmModal(false);
      } else if (checkProcessStatusState.error === 'ProcessInUse') {
        //Take String to some const
        setShowConfirmModal(true); //<-- this will use ErrorMessageData
        setShowCompletedProcessErrorModal(false);
      }
      // toast.error(formatMessage(checkProcessStatusState.error));
      resetCheckProcessStatusState();
      //ARIF: ErrorMessageData = ""
    }
  }, [checkProcessStatusState.error]);

  const mergedTabs = useMemo(() => {
    const tabs = [];
    const isTrue = (val: any) =>
      val === true || String(val).toLowerCase() === 'true';

    productImages.forEach((img, i) => {
      const isDimensionTab =
        isTrue(img?.IsDimension) || isTrue(img?.isDimension);
      tabs.push({
        type: 'image',
        Heading: isDimensionTab
          ? formatMessage('dimension_table')
          : img?.FileDetail[0]?.Heading || formatMessage('dimension_table'),
        data: img,
        originalIndex: i,
      });
    });

    return tabs;
  }, [productImages, language]);

  const areAllTabsChecked = useMemo(() => {
    let allCheckItemsChecked = true;
    let allDimensionValuesChecked = true;
    let hasAnyDimensionCheckbox = false;
    mergedTabs.forEach((tab, uiTabIndex) => {
      const data = tab.data;
      const checkItems = data?.CheckItems ?? [];
      if (checkItems.length > 0) {
        const tabChecks = submittedCheckItems[uiTabIndex] || {};
        // const allChecked = checkItems.every(
        //   (item: any) => tabChecks[item.Id]?.isChecked === true
        // );
        const allChecked = checkItems.every((item: any) =>
          (item.CheckItemMessage ?? []).every((msg: any) => {
            const checked =
              tabChecks[msg.Id]?.isChecked !== undefined
                ? tabChecks[msg.Id]?.isChecked
                : (msg.IsChecked ?? false);
            return checked === true;
          })
        );
        if (!allChecked) {
          allCheckItemsChecked = false;
        }
      }
      const dimensionsData = data?.DimensionsData ?? [];
      dimensionsData.forEach((dim: any) => {
        if (dim.DimensionType !== 1) return;
        const values = dim.DimensionValues ?? [];
        const realValueRowIndices: number[] = [];
        values.forEach((v: any, idx: number) => {
          const { hasValue } = parseDimensionValue(v);
          if (hasValue) {
            realValueRowIndices.push(idx);
          }
        });
        if (realValueRowIndices.length === 0) return;
        hasAnyDimensionCheckbox = true;
        realValueRowIndices.forEach((rowIndex: number) => {
          const { isDefaultChecked } = parseDimensionValue(values[rowIndex]);
          const checked = getEffectiveDimensionChecked(
            dimensionChecks,
            uiTabIndex,
            rowIndex,
            dim.Id,
            isDefaultChecked
          );
          if (!checked) {
            allDimensionValuesChecked = false;
          }
        });
      });
    });
    return (
      allCheckItemsChecked &&
      (!hasAnyDimensionCheckbox || allDimensionValuesChecked)
    );
  }, [mergedTabs, submittedCheckItems, dimensionChecks]);

  // Include BOM validation: for Picking/FollowUpPicking process types, require each BOM row to have Shortage or Pick checked
  const isBOMValidated = useMemo(() => {
    try {
      if (
        processType !== ProcessTypeEnum.Picking &&
        processType !== ProcessTypeEnum.FollowUpPicking
      )
        return true;
      const bomRows =
        bomDataListState && bomDataListState.length > 0
          ? bomDataListState
          : bomDataList;
      if (!bomRows || bomRows.length === 0) return true;
      const isTrue = (value: any) =>
        value === true || value === 1 || String(value).toLowerCase() === 'true';
      return bomRows.every(
        (row: any) => !!row && (isTrue(row.Shortage) || isTrue(row.Pick))
      );
    } catch (e) {
      return true;
    }
  }, [processType, bomDataListState, bomDataList]);

  const tabValidationMap = useMemo(() => {
    const validationMap: Record<number, boolean> = {};

    mergedTabs.forEach((tab, uiTabIndex) => {
      const data = tab.data;
      const checkItems = data?.CheckItems ?? [];
      const tabChecks = submittedCheckItems[uiTabIndex] || {};
      const allCheckItemsChecked =
        checkItems.length === 0 ||
        checkItems.every((item: any) =>
          (item.CheckItemMessage ?? []).every((msg: any) => {
            const checked =
              tabChecks[msg.Id]?.isChecked !== undefined
                ? tabChecks[msg.Id]?.isChecked
                : (msg.IsChecked ?? false);
            return checked === true;
          })
        );

      let allDimensionValuesChecked = true;
      const dimensionsData = data?.DimensionsData ?? [];
      dimensionsData.forEach((dim: any) => {
        if (dim.DimensionType !== 1) return;

        (dim.DimensionValues ?? []).forEach((value: any, rowIndex: number) => {
          const { hasValue, isDefaultChecked } = parseDimensionValue(value);
          if (!hasValue) return;

          const checked = getEffectiveDimensionChecked(
            dimensionChecks,
            uiTabIndex,
            rowIndex,
            dim.Id,
            isDefaultChecked
          );
          if (!checked) {
            allDimensionValuesChecked = false;
          }
        });
      });

      validationMap[uiTabIndex] =
        allCheckItemsChecked && allDimensionValuesChecked && isBOMValidated;
    });

    return validationMap;
  }, [mergedTabs, submittedCheckItems, dimensionChecks, isBOMValidated]);

  const singleMode = useOperatorAssignSingle({
    enabled: isBatch === false,
    taskId,
    processType,
    OperatorResponse,
    getOperatorState,
    resetOperatorResponse,
    resetCompleteProcessState,
    completeProcessState,
    removeForAbnormalityState,
    resetRemoveForAbnormalityState,
    GetOperatorService,
    CompleteProcessService,
    UpdateProcessService,
    startWebWorkerTimer,
    stopWebWorkerTimer,
    resetWebWorkerTimer,
    onSaveTeplarOrder: handleSaveTeplarOrder,
    bulidCheckItemsPayload,
    buildMemoPayload,
    buildDimensionPayload,
    buildBOMCheckListPayload,
    mergedTabs,
    trayHistory,
    setTrayHistory,
    trayIndex,
    setTrayIndex,
    selectedTrayId,
    setSelectedTrayId,
    setActiveIndex,
    setSubmittedCheckItems,
    setMemo,
    setDimensionChecks,
    setCanvasDrawings,
    setBomDataListState,
    setShowDraggableStart,
    startDateTime,
    setStartDateTime,
    startTimer,
    setStartTimer,
    timer,
    setTimer,
    webWorkerTime,
    setWebWorkerTime,
    isTimerRunning,
    setIsTimerRunning,
    isProcessStarted,
    setIsProcessStarted,
    operatorCount,
    setOperatorCount,
    scanBuffer,
    setScanBuffer,
    lastKeyTime,
    setLastKeyTime,
    traySelectRef,
    removeTaskToggleHandler: props.removeTaskToggleHandler,
    setRemoveDuration,
    areAllTabsChecked,
    isBOMValidated,
    bomDataList,
    submittedCheckItems,
    memo,
    dimensionChecks,
    canvasDrawings,
    bomDataListState,
    resetGetOperatorState,
    buildTeplarListPayload,
    teplarList,
    setTeplarList,
    operatorTeplarDetails,
    isRemoveForAbnormalityClicked,
    setIsRemoveForAbnormalityClicked,
    saveClicked,
    setSaveClicked,
    ledCutList,
    ledCutListState,
    setLedCutListState,
    ledLengthList,
    ledLengthListState,
    setLedLengthListState,
    correctionRequestToggleHandler: props.correctionRequestToggleHandler,
    nonConformityToggleHandler: props.nonConformityToggleHandler,
    openNonConformity,
    setOpenNonConformity,
    submitNonConformanceWorkState,
    SubmitNonConformanceWork,
    updateProcessState,
    resetUpdateProcessState,
    showConfirmModal,
    CheckProcessStatus,
    checkProcessStatusState,
  });

  const batchMode = useOperatorAssignBatch({
    enabled: isBatch === true,
    taskId,
    processType,
    OperatorResponse,
    removeForAbnormalityState,
    GetOperatorService,
    CompleteProcessService,
    completeProcessState,
    resetCompleteProcessState,
    UpdateProcessService: UpdateProcessService,
    startWebWorkerTimer,
    stopWebWorkerTimer,
    resetWebWorkerTimer,
    onSaveTeplarOrder: handleSaveTeplarOrder,
    bulidCheckItemsPayload,
    buildMemoPayload,
    buildDimensionPayload,
    buildBOMCheckListPayload,
    mergedTabs,
    trayHistory,
    setTrayHistory,
    trayIndex,
    setTrayIndex,
    selectedTrayId,
    setSelectedTrayId,
    setActiveIndex,
    setSubmittedCheckItems,
    setMemo,
    setDimensionChecks,
    setCanvasDrawings,
    setBomDataListState,
    startDateTime,
    setStartDateTime,
    startTimer,
    setStartTimer,
    timer,
    setTimer,
    webWorkerTime,
    setWebWorkerTime,
    isTimerRunning,
    setIsTimerRunning,
    isProcessStarted,
    setIsProcessStarted,
    operatorCount,
    setOperatorCount,
    scanBuffer,
    setScanBuffer,
    lastKeyTime,
    setLastKeyTime,
    traySelectRef,
    areAllTabsChecked,
    isBOMValidated,
    bomDataList,
    submittedCheckItems,
    memo,
    dimensionChecks,
    canvasDrawings,
    bomDataListState,
    setShowDraggableStart,
    resetOperatorResponse,
    buildTeplarListPayload,
    teplarList,
    setTeplarList,
    getOperatorState,
    resetGetOperatorState,
    isAddTrayInput,
    setIsAddTrayInput,
    operatorTeplarDetails,
    ledCutList,
    ledCutListState,
    setLedCutListState,
    ledLengthList,
    ledLengthListState,
    setLedLengthListState,
    correctionRequestToggleHandler: props.correctionRequestToggleHandler,
    nonConformityToggleHandler: props.nonConformityToggleHandler,
    openNonConformity,
    setOpenNonConformity,
    submitNonConformanceWorkState,
    SubmitNonConformanceWork,
    updateProcessState,
    resetUpdateProcessState,
    showConfirmModal,
    CheckProcessStatus,
    checkProcessStatusState,
  });

  const activeMode = isBatch ? batchMode : singleMode;

  useEffect(() => {
    if (OperatorResponse.IsNC) {
      setIsNonConformity(true);
    }
  }, [OperatorResponse]);

  useEffect(() => {
    if (saveTeplarOrderState.success) {
      if (saveClicked) {
        toast.success(formatMessage('updated_success'));
      }
      resetSaveTeplarOrderState();
    }
  }, [saveTeplarOrderState.success]);

  useEffect(() => {
    if (saveTeplarOrderState.error) {
      toast.error(formatMessage(saveTeplarOrderState.error));
      resetSaveTeplarOrderState();
    }
  }, [saveTeplarOrderState.error]);

  useEffect(() => {
    if (ledBlinkState.success) {
      toast.success(formatMessage('led_success'));
      resetLedBlinkState();
    }
  }, [ledBlinkState.success]);

  useEffect(() => {
    if (ledBlinkState.error) {
      toast.error(formatMessage(ledBlinkState.error));
      resetLedBlinkState();
    }
  }, [ledBlinkState.error]);

  useEffect(() => {
    if (addCorrectionRequestState.success) {
      toast.success(formatMessage('added_success'));
      resetAddCorrectionRequestState();
      GetPendingCountService(Number(getTenantIdFromLocalStorage()));
    }
  }, [addCorrectionRequestState.success]);

  useEffect(() => {
    if (addCorrectionRequestState.error) {
      toast.error(formatMessage(addCorrectionRequestState.error));
      resetAddCorrectionRequestState();
    }
  }, [addCorrectionRequestState.error]);

  useEffect(() => {
    if (addNCRequestState.success) {
      // toast.success(formatMessage('added_success'));
      resetAddNCRequestState();
    }
  }, [addNCRequestState.success]);

  useEffect(() => {
    if (addNCRequestState.error) {
      toast.error(formatMessage(addNCRequestState.error));
      resetAddNCRequestState();
    }
  }, [addNCRequestState.error]);

  useEffect(() => {
    if (openNonConformity) {
      props.nonConformityToggleHandler();
      setOpenNonConformity(false);
      if (OperatorResponse?.NCId > 0) {
        GetNonConformanceByIdService(
          Number(getTenantIdFromLocalStorage()),
          OperatorResponse?.NCId
        );
      } else {
        resetNonConformity();
      }
    }
  }, [openNonConformity]);

  const isFollowUpPicking = processType === ProcessTypeEnum.FollowUpPicking;

  const infoFields = [
    {
      labelKey: 'product_id',
      value: OperatorResponse.ProductName,
      colWidth: 1,
    },
    {
      labelKey: 'kishu',
      value:
        `${OperatorResponse?.KishuMark || ''} ${OperatorResponse?.Kishu || ''}`.trim(),
      colWidth: isFollowUpPicking ? 1 : 2,
    },
    {
      labelKey: 'katashiki',
      value: OperatorResponse.Katashiki1 + OperatorResponse.Katashiki2,
      colWidth: isFollowUpPicking ? 3 : 5,
    },
    {
      labelKey: 'quantity',
      value: OperatorResponse.Quantity,
      colWidth: 1,
    },
    {
      labelKey: 'end_user_order_id',
      value: OperatorResponse.EndUserOrderId,
      colWidth: 2,
    },
    ...(isFollowUpPicking
      ? [
          {
            labelKey: 'previous_process',
            value: OperatorResponse.PreviousProcess,
            colWidth: 2,
          },
          {
            labelKey: 'next_process',
            value: OperatorResponse.NextProcess,
            colWidth: 2,
          },
        ]
      : []),
    ...(OperatorResponse.IsNC === true ||
    (OperatorResponse?.NCStatus != null && OperatorResponse.NCStatus > -1)
      ? [
          {
            labelKey: 'reprocess_items',
            value: 'Reprocess Items',
            colWidth: 1,
          },
        ]
      : []),
  ];

  // Latest values for the navigation blocker below, which is registered only once.
  const isTimerRunningRef = useRef(isTimerRunning);
  const handlePauseTimerRef = useRef(activeMode.handlePauseTimer);
  isTimerRunningRef.current = isTimerRunning;
  handlePauseTimerRef.current = activeMode.handlePauseTimer;

  // Intercepts navigation away (sidebar/module links, browser back) while a process is
  // running. After the user confirms, the process is paused first (API call with the current
  // tray data) and only then is the originally requested navigation completed.
  useEffect(() => {
    const unblock = history.block((location) => {
      if (!isTimerRunningRef.current) return undefined;
      if (!window.confirm(formatMessage('exit_warning'))) return false;

      // Navigate only after the pause API responds; on failure stay here so the error popup
      // shows and the user can retry.
      Promise.resolve(handlePauseTimerRef.current()).then((paused) => {
        if (paused === false) return;
        isTimerRunningRef.current = false;
        unblock();
        history.push(location.pathname + location.search);
      });
      return false;
    });
    return () => unblock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBackClick = async () => {
    // Pause your timer immediately
    if (trayHistory.length > 0 && isTimerRunning) {
      isTimerRunningRef.current = false;
      activeMode.handlePauseTimer();
    }

    // Perform cleanup before navigating
    await resetOperatorResponse();
    await resetCompleteProcessState();

    // Once cleanup is done, navigate
    history.push(RoutesConstants.OperatorListDetail);
  };

  const validFiles = (productImages[activeIndex]?.FileDetail ?? []).filter(
    (file) => file.S3FilePath !== null
  );

  const closeRemoveForAbnormality = () => {
    if (isRemoveForAbnormalityClicked) {
      setIsTimerRunning(true);
      setIsProcessStarted(true);
      startWebWorkerTimer();
    }
    props.removeTaskToggleHandler();
  };

  const allFilesAreNull =
    productImages &&
    productImages.length > 0 &&
    dims.length === 0 &&
    validFiles.length === 0;

  const openLEDHandler = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.stopPropagation();
    props.addToggleHandler();
  };

  /**
   * This function is used to pause the running process when the operator opens a tool's
   * inspection tab from the Tool BOM tab's Target Tool List.
   */
  const handleToolInspectionOpen = () => {
    if (!isBatch && isTimerRunning) {
      singleMode.handlePauseTimer();
    }
  };

  const viewProps = {
    operatorTeplarDetails,
    mergedTabs,
    activeIndex,
    setActiveIndex,
    setMemo,
    setSubmittedCheckItems,
    dimensionTable,
    dims,
    dimensionChecks,
    handleDimensionCheck,
    isTimerRunning,
    productImages,
    processType,
    isProcessStarted,
    handleDecrementPrintCount,
    printCount,
    handleChangePrintCount,
    handleIncrementPrintCount,
    stringToHex,
    onSaveTeplarOrder: handleSaveTeplarOrder,
    checkItems,
    submittedCheckItems,
    handleMemoChange,
    memo,
    // handleInfoChange,
    validFiles,
    allFilesAreNull,
    canvasDrawings,
    handleCanvasChange,
    bomDataListState,
    setBomDataListState,
    tabValidationMap,
    showValidationWarnings: isTimerRunning,
    onTeplarOrderChange: setTeplarOrderData,
    bomDataList,
    teplarList,
    setTeplarList,
    saveClicked,
    setSaveClicked,
    ledCutList,
    ledCutListState,
    setLedCutListState,
    ledLengthList,
    ledLengthListState,
    setLedLengthListState,
    openNonConformity,
    setOpenNonConformity,
    // correctionRequestToggleHandler: props.correctionRequestToggleHandler,
    onToolInspectionOpen: handleToolInspectionOpen,
    showStart,
    setShowStart,
  };

  const confirmToggleHandler = () => {
    setShowConfirmModal(true);
  };

  const removeTrayHandler = () => {
    if (isBatch && showCompletedProcessErrorModal) {
      const completedSet = new Set(
        completedTrays.map((tray) => String(tray).trim())
      );
      setTrayHistory((prev) => {
        const updatedTrayHistory = prev.filter(
          (tray) => !completedSet.has(String(tray).trim())
        );
        setSelectedTrayId(
          updatedTrayHistory.length > 0
            ? updatedTrayHistory[updatedTrayHistory.length - 1]
            : null
        );
        return updatedTrayHistory;
      });

      // batchMode.handleRemoveTray();
    }
    setShowConfirmModal(false);
    setShowCompletedProcessErrorModal(false);
    if (isBatch) {
      setIsTimerRunning(false);
      setTimer(0);
      resetWebWorkerTimer();
      setIsProcessStarted(false);
    } else {
      singleMode.handleRemoveTray();
      setShowStart(true);
      setIsTimerRunning(false);
      // setTimer(startTimer);
      // resetWebWorkerTimer();
    }
  };

  useEffect(() => {
    if (completeProcessState.error) {
      setShowConfirmSubmitModal(true);
    }
  }, [completeProcessState.error]);

  useEffect(() => {
    if (submitNonConformanceWorkState.success) {
      // Submitted from Pause (popup closed) - don't complete the process
      if (!props.showNonConformityPopup) {
        resetSubmitNCWorkState();
        return;
      }
      singleMode.handleCompleteProcess();
    }
  }, [submitNonConformanceWorkState.success]);

  // Fallback to the latest scanned tray if selectedTrayId is temporarily null/unset
  // to ensure child components and dropdown menus always have an active selection.
  const effectiveSelectedTrayId =
    selectedTrayId ||
    (trayHistory.length > 0 ? trayHistory[trayHistory.length - 1] : null);

  const taskContextValue: OperatorTaskContextType = {
    taskId,
    processName,
    processType,
    selectedTrayId: effectiveSelectedTrayId,
    trayHistory,
    isProcessStarted,
    activeSize,
    formattedTime: formatTime(webWorkerTime),
    operatorCount,
    infoFields,
    buttons: activeMode.buttons,
    showKeepToolPopup: singleMode.showKeepToolPopup,
    handleKeepToolSave: singleMode.handleKeepToolSave,
    handleKeepToolClose: singleMode.handleKeepToolClose,
    showAddTrayInput,
    trayId,
    setTrayId,
    addTray: () => {
      activeMode.addTray(trayId);
      setTrayId('');
    },
    handleBackClick,
    openLEDHandler,
    handleFontSizeChange,
    handleDecrement,
    handleIncrement,
    handleChange,
    handleTraySelectChange: activeMode.handleTraySelectChange,
    shouldShowTrayWarning: activeMode.shouldShowTrayWarning,
    shouldShowCheckMark: activeMode.shouldShowCheckMark,
    traySelectRef,
    showConfirmModal,
    isAddTrayInput,
    setIsAddTrayInput,
    openNonConformity,
    setOpenNonConformity,
    operatorResponseViewType: OperatorResponse.ViewType,
    viewProps,
    isTimerRunning,
    showDraggableStart,
    setShowDraggableStart,
    handleSubmitNCWork: SubmitNonConformanceWork,
  };

  return (
    <OperatorTaskProvider value={taskContextValue}>
      <div className={`operator-wrapper ${fontSizeClass}`}>
        {showModeSelectionPopup && (
          <ModeSelectionModal
            show={showModeSelectionPopup}
            onModalClosed={() => setShowModeSelectionPopup(false)}
            onSelectMode={(isBatch: boolean) => setIsBatch(isBatch)}
            handleBackClick={handleBackClick}
          />
        )}
        {props.showRemoveTaskPopup && (
          <RemoveTask
            id={selectedTrayId}
            modalClosed={closeRemoveForAbnormality}
            Count={1}
            ProcessId={taskId}
            startTime={startDateTime}
            Duration={removeDuration}
            NoOfOperators={operatorCount}
            CheckItemList={bulidCheckItemsPayload()}
            MemoMessage={buildMemoPayload()}
            Dimensions={buildDimensionPayload()}
            BOM={buildBOMCheckListPayload()}
            TeplarList={buildTeplarListPayload()}
            processType={processType}
          />
        )}
        {props.showPrintContentPopup && (
          <PrintContent
            id={Number(selectedTrayId)}
            modalClosed={props.printToggleHandler}
            printContents={printContents}
            templateName={templateName}
          />
        )}
        {props.showLEDBlinkPopup && (
          <LedBlink
            id={OperatorResponse?.Id}
            modalClosed={props.addToggleHandler}
            selectedRows={[OperatorResponse?.TrayId]}
            isToolLed={false}
            toolId={''}
          />
        )}
        {props.showCorrectionRequestPopup && (
          <CorrectionRequest
            id={OperatorResponse?.Id}
            modalClosed={props.correctionRequestToggleHandler}
            ProcessId={Number(taskId)}
            ProductId={OperatorResponse?.ProductId}
            ProductName={OperatorResponse?.ProductName}
            TrayId={OperatorResponse?.Id}
          />
        )}
        {props.showNonConformityPopup && (
          <AddUpdateNonConformity
            id={OperatorResponse?.Id}
            modalClosed={props.nonConformityToggleHandler}
            ProductId={OperatorResponse?.ProductId}
            ProductName={OperatorResponse?.ProductName}
            ProcessId={Number(taskId)}
            TrayId={OperatorResponse?.TrayId}
            Katashiki={
              (OperatorResponse?.Katashiki1 || '') +
              (OperatorResponse?.Katashiki2 || '')
            }
            Kishu={OperatorResponse?.Kishu}
            processName={processName}
            quantity={OperatorResponse?.Quantity}
            IsNC={OperatorResponse?.IsNC}
            NCID={OperatorResponse?.NCId}
            NGID={OperatorResponse?.NGId}
            status={OperatorResponse?.NCStatus}
            StartTime={startDateTime}
            Duration={webWorkerTime - startTimer}
            isBatchMode={processType === 2}
            selectedTrays={trayHistory}
            onPauseRunningTimer={() => {
              if (isTimerRunning) {
                activeMode?.handlePauseTimer?.();
              }
            }}
            onSuccess={(reportedTrayId) => {
              if (!isBatch) {
                singleMode.handleNonConformitySuccess();
                return;
              }
              batchMode.handleNonConformitySuccess(reportedTrayId);
            }}
          />
        )}

        {(showConfirmModal || showCompletedProcessErrorModal) && (
          <ProcessInUseConfirmPopup
            showConfirmModal={showConfirmModal}
            setShowConfirmModal={setShowConfirmModal}
            handleStartTimer={() => {
              setShowStart(false);
              activeMode.handleStartTimer();
            }}
            handleRemoveTray={removeTrayHandler}
            isLoading={false}
            title={''}
            showCompletedProcessErrorModal={showCompletedProcessErrorModal}
            setShowCompletedProcessErrorModal={
              setShowCompletedProcessErrorModal
            }
            message={
              showConfirmModal
                ? formatMessage('process_in_use_confirm')
                : formatMessage('process_completed_please_remove')
            }
            error={inProgressOrCompletedTrays}
            isBatch={isBatch}
          />
        )}
        {showConfirmSubmitModal && (
          <ProcessPauseConfirmPopup
            showConfirmSubmitModal={showConfirmSubmitModal}
            setShowConfirmSubmitModal={setShowConfirmSubmitModal}
            handleRemoveTray={() => {
              if (isBatch) {
                const completedSet = new Set(
                  completedTrays.map((tray) => String(tray).trim())
                );
                setTrayHistory((prev) => {
                  const updatedTrayHistory = prev.filter(
                    (tray) => !completedSet.has(String(tray).trim())
                  );
                  setSelectedTrayId(
                    updatedTrayHistory.length > 0
                      ? updatedTrayHistory[updatedTrayHistory.length - 1]
                      : null
                  );
                  return updatedTrayHistory;
                });
              } else {
                activeMode.handleRemoveTray();
              }
              if (isPaused) {
                activeMode.PausedSuccess();
              } else {
                setIsTimerRunning(false);
                setTimer(0);
                resetWebWorkerTimer();
                setIsProcessStarted(false);
              }
              resetCompleteProcessState();
            }}
            isLoading={false}
            title={''}
            message={formatMessage('trays_owned_by_other_users')}
            error={inProgressOrCompletedTrays}
            isBatch={false}
          />
        )}
        {(completeProcessState.inProgress ||
          getOperatorState.inProgress ||
          removeForAbnormalityState.inProgress ||
          addNCRequestState.inProgress ||
          submitNonConformanceWorkState.inProgress ||
          getNonConformanceByIdState.inProgress ||
          updateProcessState.inProgress ||
          checkProcessStatusState.inProgress) && (
          <FormLoader
            loading={
              completeProcessState.inProgress ||
              getOperatorState.inProgress ||
              removeForAbnormalityState.inProgress ||
              addNCRequestState.inProgress ||
              submitNonConformanceWorkState.inProgress ||
              getNonConformanceByIdState.inProgress ||
              updateProcessState.inProgress ||
              checkProcessStatusState.inProgress
            }
          />
        )}
        {isBatch === null ? null : isBatch ? (
          <OperatorAssignBatchMode />
        ) : (
          <OperatorAssignSingleMode />
        )}
      </div>
    </OperatorTaskProvider>
  );
};

export default NonConformityPopupHOC(
  CorrectionRequestPopupHOC(
    LEDBlinkPopupHOC(
      PrintContentPopupHOC(RemoveTaskPopupHOC(observer(OperatorAssignTask)))
    )
  )
);
